import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth";

/* /smartket/join — Role picker. First choice a visitor makes on
   entering the marketplace. Directs them to the right side of the
   product so we never dump a marketer form on a founder or vice
   versa. */

export default function SmartKetJoin() {
  const { user, loading } = useAuth();
  useEffect(() => {
    const prev = document.title;
    document.title = "SmartKet — Which door are you walking through?";
    return () => { document.title = prev; };
  }, []);

  // While /auth/me is still resolving, avoid flashing a "sign in" CTA to
  // an already signed-in user — show a neutral loading dash on the CTAs.
  const authReady = !loading;

  return (
    <div data-testid="smartket-join" className="min-h-screen bg-[#0d1410] text-white">
      <div className="border-b-2 border-[#d4a34a]/40 bg-[#141a15]">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
          <Link to="/smartket" className="font-mono text-[10px] tracking-[0.4em] text-white/60 hover:text-[#d4a34a]">
            ← SMARTKET
          </Link>
          <h1 className="mt-3 font-display text-4xl md:text-6xl leading-none tracking-tight">
            WHICH DOOR<br/>ARE YOU <span className="text-[#d4a34a]">WALKING THROUGH</span>?
          </h1>
          <p className="mt-3 font-mono text-sm text-white/75 max-w-xl">
            Pick the side that fits. You can always come back and open the other one later.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* FOUNDER PATH */}
        <div className="border-2 border-white/15 bg-gradient-to-br from-[#1a3a2a] to-[#0d1410] p-6 flex flex-col hover:border-white/40 transition-colors">
          <div className="font-mono text-[10px] tracking-[0.4em] text-white/60 mb-2">▸ FOR THE FOUNDER</div>
          <div className="font-display text-3xl md:text-4xl leading-none mb-3">
            I HAVE A<br/>PRODUCT.
          </div>
          <p className="font-mono text-xs md:text-sm text-white/80 leading-relaxed mb-6 flex-1">
            You've built the thing — a product, an app, a service. You just need people to actually see it and buy it. List it. Pick a commission %. Pay nothing until money lands.
          </p>
          <ul className="font-mono text-xs text-white/70 space-y-1 mb-6">
            <li>▸ List free. No monthly cost.</li>
            <li>▸ Pick from 3 commission templates.</li>
            <li>▸ Only pay marketers on verified sales.</li>
          </ul>
          <Link
            to={user ? "/smartket/board?list=1" : "/login?next=/smartket/board?list=1"}
            data-testid="join-founder-cta"
            className={`bg-white text-[#1a3a2a] font-display text-base tracking-widest text-center px-4 py-3 shadow-brutal hover:bg-[#d4a34a] ${!authReady ? "opacity-60 pointer-events-none" : ""}`}
          >
            {!authReady ? "…" : user ? "LIST YOUR PRODUCT ▸" : "SIGN IN TO LIST ▸"}
          </Link>
        </div>

        {/* MARKETER PATH */}
        <div className="border-2 border-white/15 bg-gradient-to-br from-[#c98a3a] to-[#3a2410] p-6 flex flex-col hover:border-white/40 transition-colors">
          <div className="font-mono text-[10px] tracking-[0.4em] text-[#3a2410] mb-2">FOR THE MARKETER ◂</div>
          <div className="font-display text-3xl md:text-4xl leading-none mb-3 text-[#2a1a08]">
            I CAN<br/>MARKET.
          </div>
          <p className="font-mono text-xs md:text-sm text-[#2a1a08]/90 leading-relaxed mb-6 flex-1">
            You're sharp with growth, ads, content, SEO, or community. Build your marketer profile, browse products you actually believe in, and pull a slip to earn commission on real sales.
          </p>
          <ul className="font-mono text-xs text-[#2a1a08]/85 space-y-1 mb-6">
            <li>▸ Public profile with niches + portfolio.</li>
            <li>▸ Pull a slip = signed contract with agreed terms.</li>
            <li>▸ Bi-weekly Stripe payouts on verified sales.</li>
          </ul>
          <Link
            to={user ? "/smartket/marketer/new" : "/login?next=/smartket/marketer/new"}
            data-testid="join-marketer-cta"
            className={`bg-[#2a1a08] text-white font-display text-base tracking-widest text-center px-4 py-3 shadow-brutal hover:bg-black ${!authReady ? "opacity-60 pointer-events-none" : ""}`}
          >
            {!authReady ? "…" : user ? "◂ CREATE MARKETER PROFILE" : "◂ SIGN IN TO BUILD PROFILE"}
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-8 pb-16 space-y-4">
        <div className="border-2 border-[#d4a34a]/60 bg-[#1a1410] p-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] tracking-widest text-[#d4a34a] mb-1">NEW HERE? START WITH THE ORIENTATION</div>
            <div className="font-mono text-sm text-white/85">See exactly how SmartKet works. 4 steps for founders, 4 for marketers, plain English.</div>
          </div>
          <Link to="/smartket/how-it-works" data-testid="join-hiw-cta"
                className="bg-[#d4a34a] text-black font-display text-sm tracking-widest px-4 py-3 shadow-brutal hover:bg-white transition-colors">
            HOW IT WORKS ▸
          </Link>
        </div>
        <div className="border border-white/10 bg-[#141a15] p-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] tracking-widest text-[#d4a34a] mb-1">JUST BROWSING?</div>
            <div className="font-mono text-sm text-white/80">Peek at the Product Board and see what founders are shipping.</div>
          </div>
          <Link
            to="/smartket/board"
            data-testid="join-browse-cta"
            className="bg-[#d4a34a] text-black font-mono text-[11px] tracking-widest px-4 py-2 hover:bg-white transition-colors"
          >
            BROWSE THE BOARD ▸
          </Link>
        </div>
        <div className="border border-white/10 bg-[#141a15] p-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] tracking-widest text-[#d4a34a] mb-1">WANT THE RULES FIRST?</div>
            <div className="font-mono text-sm text-white/80">Read the Slip Agreement. Plain English on top, full legal below.</div>
          </div>
          <Link
            to="/smartket/terms"
            data-testid="join-terms-cta"
            className="bg-white/10 border border-white/20 hover:bg-white/20 font-mono text-[11px] tracking-widest px-4 py-2 transition-colors"
          >
            READ THE TERMS ▸
          </Link>
        </div>
      </div>
    </div>
  );
}
